// =============================================================================
// ui/tabs/ConfluenceEditor.tsx — Settings → Preferences → Trade Form Confluences.
// -----------------------------------------------------------------------------
// The checkboxes offered when annotating a trade, and their sub-confluences.
//
// ── 🔴 SETTINGS ONLY, AND THAT IS A SAFETY DECISION ──
//
// The obvious extra is a "+ add confluence" on the trade form itself, where a
// trader notices the gap. It is refused, and the reason is specific: THIS screen
// refuses to draw until the stored preferences have actually been read back,
// while the trade form, the backtest form and the bulk-edit box all fall back to
// the six built-in confluences whenever the real list has not arrived. An add
// button on any of those screens would, on one press during a slow read, write
// six starters over a hand-built vocabulary with no undo.
//
// ── 🔴 NO "RESET TO DEFAULTS" BUTTON, DELIBERATELY ──
//
// Beta has one. It replaces the entire list — every confluence and every
// sub-confluence the trader ever built — with the six generic starters, with no
// undo, under a confirmation reading "existing trade data is preserved". That
// sentence is true of the trades and reads as though nothing is lost. Deleting
// every row one at a time reaches the same place, slowly enough to change your
// mind, and an emptied list falls back to the defaults anyway.
// =============================================================================

import { useState } from 'react';
import { usePreferences } from '../state/preferences';
import {
  CONFLUENCE_NAME_MAX, addChild, addConfluence, allConfluenceIds, confluenceRegistry,
  registryToStore, removeChild, removeConfluence, renameConfluence,
  type ConfluenceNode,
} from '../../selectors/confluences';

export function ConfluenceEditor() {
  const { prefs, update } = usePreferences();
  const registry = confluenceRegistry(prefs?.confluences);

  const [draft, setDraft] = useState('');
  const [childDraft, setChildDraft] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<string | null>(null);

  /**
   * ⚠ IDENTITY, NOT A DEEP COMPARE, and it is the whole data-loss guard.
   *
   * The six built-in confluences live only in the app's source, hints included,
   * and the registry falls back to them while the stored list is empty. The
   * first write of a non-empty array ends that for ever. Every mutator returns
   * the SAME array when it would change nothing, so tabbing through a name box
   * cannot be what materialises six strangers' entries into a real account.
   */
  const commit = (next: readonly ConfluenceNode[]): void => {
    if (next === registry) return;
    void update({ confluences: registryToStore(next) });
  };

  const taken = allConfluenceIds(registry);

  return (
    <section className="card" data-testid="confluences">
      <h3>Trade form confluences ({registry.length})</h3>
      <p className="muted">
        The reasons you can tick when annotating a trade. Give one
        sub-confluences to break it down — “Orderflow” into “delta flip” and
        “absorption”, say — and the trade form offers those instead of the
        heading.
      </p>

      <div className="conf-rows">
        {registry.map((c) => (
          <div className="conf-row" key={c.id}>
            <div className="conf-main">
              {/* ⚠ THE ID IS SHOWN AND NEVER EDITABLE. Trades store ids, not
                  names, so it has to outlive every rename — and showing it is
                  what makes a stray code in the analytics leaderboard
                  identifiable later. */}
              <span className="conf-id mono muted">{c.id}</span>
              <input
                type="text" className="conf-name" defaultValue={c.label}
                maxLength={CONFLUENCE_NAME_MAX} aria-label={`Name for ${c.label}`}
                onBlur={(e) => commit(renameConfluence(registry, c.id, e.target.value))}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              />
              {confirming === c.id ? (
                <>
                  <button
                    type="button" className="btn danger btn-sm"
                    onClick={() => { setConfirming(null); commit(removeConfluence(registry, c.id)); }}
                  >
                    Delete “{c.label}”{c.children.length ? ` and ${c.children.length} sub` : ''}
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => setConfirming(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-sm" onClick={() => setConfirming(c.id)}>
                  Delete
                </button>
              )}
            </div>

            <div className="conf-subs">
              <span className="conf-sub-label muted">SUB</span>
              {c.children.map((ch) => (
                <span className="conf-chip" key={ch.id} title={`id: ${ch.id}`}>
                  <input
                    type="text" className="conf-chip-name" defaultValue={ch.label}
                    maxLength={CONFLUENCE_NAME_MAX} aria-label={`Name for ${ch.label}`}
                    onBlur={(e) => commit(renameConfluence(registry, ch.id, e.target.value))}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  />
                  <button
                    type="button" className="btn danger btn-sm" aria-label={`Remove ${ch.label}`}
                    onClick={() => commit(removeChild(registry, c.id, ch.id))}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text" className="conf-sub-add" placeholder="+ add sub-confluence, Enter"
                maxLength={CONFLUENCE_NAME_MAX} aria-label={`Add a sub-confluence under ${c.label}`}
                value={childDraft[c.id] ?? ''}
                onChange={(e) => setChildDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  commit(addChild(registry, c.id, childDraft[c.id] ?? '', taken));
                  setChildDraft((d) => ({ ...d, [c.id]: '' }));
                }}
              />
            </div>

            {/* 🚨 THE WARNING THAT STOPS A SETTINGS EDIT SILENTLY RESTATING THE
                BOOK. A confluence WITH sub-confluences becomes a heading and
                stops counting on its own — its subs carry the signal, and
                counting both would count one observation twice. So adding the
                first sub to a well-used confluence drops every trade whose only
                mark was that heading to zero marks, and the Confluence Edge card
                moves. Said before it happens, on the row it happens to. */}
            {c.children.length === 0 ? (
              <p className="muted conf-note">
                Adding a sub-confluence turns “{c.label}” into a heading: trades
                already ticked with it stop counting as tagged until you tick one
                of its subs, and the Confluence Edge ranking will change.
              </p>
            ) : null}
          </div>
        ))}
      </div>

      <div className="conf-add">
        <input
          type="text" placeholder="New confluence name" maxLength={CONFLUENCE_NAME_MAX}
          aria-label="New confluence name"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            commit(addConfluence(registry, draft, taken));
            setDraft('');
          }}
        />
        <button
          type="button" className="btn" disabled={!draft.trim()}
          onClick={() => { commit(addConfluence(registry, draft, taken)); setDraft(''); }}
        >
          + Add confluence
        </button>
      </div>

      {/* ⚠ BOTH FACTS, because each one is surprising on its own and neither is
          recoverable from this screen. */}
      <p className="muted conf-note">
        Renaming is safe and instant — trades record the code on the left, never
        the name, so every past trade shows the new one. <b>Deleting is not:</b> a
        removed confluence stays on every trade that carried it and keeps
        appearing in the Confluence Edge ranking under its code rather than its
        name. Deleting a confluence deletes its sub-confluences too.
      </p>
    </section>
  );
}
